import { multiPolygon } from '@turf/turf'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useAssetLocations } from '../../../../data/locations.tsx'
import { bufferGeometry } from '../buffer/bufferGeometry.ts'
import { combineGeometries, DEFAULT_BUFFER_STEPS } from '../buffer/bufferReportGeometry.ts'
import { type BaseReportForm, useBaseFormValues } from '../schema.ts'

// Component instead of a hook to reduce re-rendering of other components
export const UpdateGeometryWhenDependentFieldsChange = ({ maxVertexCount }: { maxVertexCount: number }) => {
	const { setValue } = useFormContext<BaseReportForm>()
	const { data: assetLocations } = useAssetLocations()
	const [assetVrns, customPoint, radiusKm] = useBaseFormValues(['assetVrns', 'customPoint', 'radiusKm'])

	// Recalculate geometry when the assets, radius or custom point change
	useEffect(() => {
		const setGeometry = (geometry: BaseReportForm['geometry'], tooComplex: boolean) => {
			setValue('geometry', geometry, { shouldValidate: true })
			setValue('geometryTooComplex', tooComplex)
			setValue('geometryPending', false)
		}
		const noGeometry = multiPolygon([]).geometry as BaseReportForm['geometry']

		const selectedGeometries = assetLocations.filter(({ assetVrn }) => assetVrns.includes(assetVrn)).map(({ geometry }) => geometry)
		const unbufferedGeometry = customPoint ? customPoint : combineGeometries(selectedGeometries)
		if (!unbufferedGeometry || customPoint?.isOutsideAssetGeometry) return setGeometry(noGeometry, false)

		setValue('geometryPending', true)
		let isCurrent = true
		void bufferGeometry({ geometry: unbufferedGeometry, radiusKm: radiusKm ?? 0, maxVertexCount, bufferSteps: DEFAULT_BUFFER_STEPS }).then(
			(result) => {
				if (!isCurrent || !result) return
				if (result.success) setGeometry(result.geometry as BaseReportForm['geometry'], false)
				else setGeometry(noGeometry, result.reason === 'geometry-too-complex')
			},
		)
		return () => {
			isCurrent = false
		}
	}, [assetLocations, assetVrns, radiusKm, customPoint, maxVertexCount])

	return null
}
